import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock-v2-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit.svg';
import { ReactComponent as ArabicFlag } from '../../../../../assets/icons/flag-icons/arabic.svg';
import { ReactComponent as EnglishFlag } from '../../../../../assets/icons/flag-icons/english.svg';
import { ReactComponent as FrenchFlag } from '../../../../../assets/icons/flag-icons/french.svg';
import { ReactComponent as SpanishFlag } from '../../../../../assets/icons/flag-icons/spanish.svg';
import { ReactComponent as LocationSelector } from '../../../../../assets/icons/location-selector.svg';
import { ReactComponent as NextArrowIcon } from '../../../../../assets/icons/next-arrow.svg';
import { ReactComponent as PreviousArrowIcon } from '../../../../../assets/icons/prev-arrow.svg';
import DefaultStationImage from '../../../../../assets/images/default-station.png';
import NoDataFound from '../../../../../assets/images/no-data-found.png';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  fetchLocationDetailsData,
  removeLocationDetails,
} from '../../../../../redux/slices/LocationDetailsSlice';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import CustomInput from '../../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../../shared-components/select-field/SelectField';
import SelectLatLong from '../../../../../shared-components/select-location/components/SelectLatLong';
import LocationSidePanel from '../../../../../shared-components/table-side-panel/LocationSidePanel';
import SidePaneModel from '../../../../../shared-components/table-side-panel/SidePaneModel';
import TabsContainer from '../../../../../shared-components/tabs-container/TabsContainer';
import { convertTo12HourFormat } from '../../../../../shared-functions/DataTime';
import { permissionCheck } from '../../../../../shared-functions/JWTDocodeFunction';
import chargingStation from '../../../../../utils/api/partner/station';
import SelectAmenities from '../../../location-management/components/SelectAmenities';

import LocationImagesModal from './LocationImagesModal';

/**
 * Helper to format open/close times to 12h format
 * @param {any} dayData - The dayData
 * @param {string} closedLabel - The closedLabel
 * @param {string} open24Label - The open24Label
 * @returns {string} Formatted operating hours string
 */
const getFormattedHours = (dayData: any, closedLabel: string, open24Label: string): string => {
  if (!dayData) return 'N/A';
  const isOpen = dayData.isOpen || dayData.status === 'open';
  if (!isOpen) return closedLabel;
  const openTime = dayData.from || dayData.open;
  const closeTime = dayData.to || dayData.close;
  if (openTime === '00:00' && closeTime === '23:59') {
    return open24Label;
  }
  const formattedOpen = openTime ? convertTo12HourFormat(openTime) : 'N/A';
  const formattedClose = closeTime ? convertTo12HourFormat(closeTime) : 'N/A';
  return `${formattedOpen} - ${formattedClose}`;
};

/**
 * Helper to get language options with country flags
 * @returns {Array<{value: string, label: string, labelNode: React.ReactNode}>} List of language options
 */
const getLanguageOptions = () => [
  {
    value: 'English',
    label: 'English',
    labelNode: (
      <div className="language-option-node">
        <EnglishFlag />
        <span>English</span>
      </div>
    ),
  },
  {
    value: 'Arabic',
    label: 'Arabic',
    labelNode: (
      <div className="language-option-node">
        <ArabicFlag />
        <span>Arabic</span>
      </div>
    ),
  },
  {
    value: 'French',
    label: 'French',
    labelNode: (
      <div className="language-option-node">
        <FrenchFlag />
        <span>French</span>
      </div>
    ),
  },
  {
    value: 'Spanish',
    label: 'Spanish',
    labelNode: (
      <div className="language-option-node">
        <SpanishFlag />
        <span>Spanish</span>
      </div>
    ),
  },
];

/**
 * Helper to append mandatory station details to FormData for editStation API call
 * @param {FormData} formData - The FormData object
 * @param {any} stationDetailsData - The current station details object
 */
const appendStationDataToFormData = (formData: FormData, stationDetailsData: any) => {
  if (!stationDetailsData) return;
  if (stationDetailsData.name) {
    formData.append('name', stationDetailsData.name);
  }
  if (stationDetailsData.accountCustomId) {
    formData.append('accountCustomId', stationDetailsData.accountCustomId);
  }
  if (stationDetailsData.latitude !== undefined && stationDetailsData.latitude !== null) {
    formData.append('latitude', String(stationDetailsData.latitude));
  }
  if (stationDetailsData.longitude !== undefined && stationDetailsData.longitude !== null) {
    formData.append('longitude', String(stationDetailsData.longitude));
  }
  if (stationDetailsData.address) {
    formData.append('address', stationDetailsData.address);
  }
  if (stationDetailsData.electricityContractType) {
    formData.append('electricityContractType', stationDetailsData.electricityContractType);
  }
  if (stationDetailsData.countryCode && stationDetailsData.emergencyContact) {
    formData.append('countryCode', stationDetailsData.countryCode);
    formData.append('emergencyContact', stationDetailsData.emergencyContact);
  } else if (stationDetailsData.emergencyContact) {
    formData.append('emergencyContact', stationDetailsData.emergencyContact);
  }
  if (
    stationDetailsData.electricityCostByUtilityProvider !== null &&
    stationDetailsData.electricityCostByUtilityProvider !== undefined &&
    String(stationDetailsData.electricityCostByUtilityProvider) !== ''
  ) {
    formData.append(
      'electricityCostByUtilityProvider',
      stationDetailsData.electricityCostByUtilityProvider.toString(),
    );
  }
  formData.append(
    'isGuestChargingExclusive',
    stationDetailsData.isGuestChargingExclusive
      ? String(stationDetailsData.isGuestChargingExclusive)
      : 'false',
  );

  const timeZone =
    stationDetailsData.time_zone || stationDetailsData.timeZone || stationDetailsData.timezone;
  if (timeZone) {
    formData.append('time_zone', timeZone);
  }

  if (stationDetailsData.operatingHours) {
    const days = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ] as const;

    days.forEach((day) => {
      const dayData = stationDetailsData.operatingHours?.[day];
      if (dayData) {
        const isOpen = dayData.isOpen || dayData.status === 'open';
        const openTime = dayData.from || dayData.open || '00:00';
        const closeTime = dayData.to || dayData.close || '23:59';

        if (isOpen) {
          formData.append(`operatingHours[${day}][open]`, openTime);
          formData.append(`operatingHours[${day}][close]`, closeTime);
          formData.append(`operatingHours[${day}][status]`, 'open');
        } else {
          formData.append(`operatingHours[${day}][status]`, 'closed');
        }
      }
    });
    if (stationDetailsData.operatingHours.allDay !== undefined) {
      formData.append('operatingHours[allDay]', String(stationDetailsData.operatingHours.allDay));
    }
  }

  if (Array.isArray(stationDetailsData.amenities)) {
    stationDetailsData.amenities.forEach((amenity: string, index: number) => {
      formData.append(`amenities[${index}]`, amenity);
    });
  }
};

/**
 * Station details component
 * @returns {any} The rendered component
 */
export function LocationDetailsLeft() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { stationDetails, isLoading } = useAppSelector((state) => state.locationDetails);
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);

  const [openTab, setOpenTab] = useState(0);
  const [editStationOpen, setEditStationOpen] = useState(false);
  const [locationPopup, setLocationPopup] = useState<string>('close');
  const [isEditingDirections, setIsEditingDirections] = useState(false);
  const [directionsListState, setDirectionsListState] = useState<
    { language: string; direction: string }[]
  >([]);
  const [isSavingDirections, setIsSavingDirections] = useState(false);

  /**
   * Fetch the account details in redux
   */
  const fetchData = () => {
    if (selectedChargingPoint?.id) {
      dispatch(fetchLocationDetailsData(selectedChargingPoint.id));
    }
  };

  /**
   * Filter and return language options not already selected
   * @param {number} currentIndex - The current index of the dropdown
   * @returns {Array} Available language options
   */
  const getAvailableLanguageOptions = (currentIndex: number) => {
    const selectedLangs = directionsListState
      .map((item: any, index: number) => (index !== currentIndex ? item.language : ''))
      .filter(Boolean);
    return getLanguageOptions().filter((option) => !selectedLangs.includes(option.value));
  };

  /**
   * Handle direction text change and enforce language characters
   * @param {number} index - The index of the row being changed
   * @param {string} value - The input text value
   */
  const handleDirectionsChange = (index: number, value: string) => {
    const lang = directionsListState[index].language;
    let filteredValue = value;
    if (lang === 'English') {
      filteredValue = value.replace(/[^a-zA-Z0-9\s.,'()"\-_?!]/g, '');
    } else if (lang === 'Arabic') {
      filteredValue = value.replace(
        /[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF0-9\s.,'()"\-_?!]/g,
        '',
      );
    } else if (lang === 'French') {
      filteredValue = value.replace(/[^a-zA-ZÀ-ÿ0-9\s.,'()"\-_?!]/g, '');
    } else if (lang === 'Spanish') {
      filteredValue = value.replace(/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ0-9\s.,'()"\-_?!]/g, '');
    }
    const updated = [...directionsListState];
    updated[index].direction = filteredValue;
    setDirectionsListState(updated);
  };

  /**
   * Add a new navigation direction row
   */
  const handleAddNavigationDirection = () => {
    if (directionsListState.length < getLanguageOptions().length) {
      setDirectionsListState([...directionsListState, { language: '', direction: '' }]);
    }
  };

  /**
   * Start editing directions inline
   */
  const handleStartEditDirections = () => {
    const existing = (stationDetails as any)?.navigationDirections || [];
    setDirectionsListState(
      existing.length > 0
        ? existing.map((item: any) => ({
            language: item.language || '',
            direction: item.direction || '',
          }))
        : [{ language: '', direction: '' }],
    );
    setIsEditingDirections(true);
  };

  /**
   * Save navigation directions to the backend
   */
  const handleSaveDirections = async () => {
    try {
      setIsSavingDirections(true);
      const formData = new FormData();
      appendStationDataToFormData(formData, stationDetails);

      const languageCountryMap: Record<string, string> = {
        Arabic: 'Saudi Arabia',
        English: 'United States',
        French: 'France',
        Spanish: 'Spain',
      };

      const activeDirections = directionsListState.filter((item) => item.language);
      activeDirections.forEach((nav, index) => {
        const country = languageCountryMap[nav.language] || '';
        formData.append(`navigationDirections[${index}][country]`, country);
        formData.append(`navigationDirections[${index}][language]`, nav.language);
        formData.append(`navigationDirections[${index}][direction]`, nav.direction);
      });

      const stationId = (stationDetails as any)?._id || (selectedChargingPoint?.id as string);
      await chargingStation.editStation(stationId, formData, () => {});

      dispatch(
        setNotificationMessage({
          status: true,
          message: t('stationUpdatedSuccessfully'),
          type: 'success',
          code: 200,
        }),
      );

      setIsEditingDirections(false);
      fetchData();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setIsSavingDirections(false);
    }
  };

  const containerTabs = [
    {
      label: t('stationDetail'),
      key: 'stationDetail',
      isActive: openTab === 0,
    },
    {
      label: t('navigationDirections'),
      key: 'navigationDirections',
      isActive: openTab === 1,
    },
  ];

  /**
   * Handles switching tabs
   * @param {string} key - Selected tab key
   */
  const handleSwitchTab = (key: string) => {
    if (key === 'stationDetail') {
      setOpenTab(0);
    } else if (key === 'navigationDirections') {
      setOpenTab(1);
    }
  };

  useEffect(() => {
    if (stationDetails && stationDetails?.submission?.status !== 'approved') {
      dispatch(removeLocationDetails());
      navigate('/ev-admin/operations/charging-location/approved');
    }
    if (selectedChargingPoint?.id) {
      fetchData();
    }
  }, [selectedChargingPoint]);

  return (
    <div className="location-details-left-location-details" dir={i18n.dir()}>
      <TabsContainer containerTabs={containerTabs} handleSwitchTab={handleSwitchTab} />

      <div className="tab-content-container">
        {(() => {
          if (isLoading) {
            return (
              <div className="skeleton-grid-loader">
                <div className="skeleton-card" />
              </div>
            );
          }

          if (!stationDetails) {
            return (
              <div className="table-no-data-wrap">
                <img src={NoDataFound} alt="" className="no-data-found-image" />
                <div className="no-data-found-details">
                  <h3>{t('noLocationDetailsTitle')}</h3>
                  <p>{t('noLocationDetailsDesc')}</p>
                </div>
              </div>
            );
          }

          if (openTab === 0) {
            const stationInfoDetails = [
              { id: 1, label: t('stationName'), key: 'name' },
              { id: 2, label: t('publiclyAccess'), key: 'isAvailable' },
              { id: 3, label: t('mapLocation'), key: 'mapLocation' },
              { id: 4, label: t('emergencyContact'), key: 'emergencyContact' },
              { id: 5, label: t('timeZone'), key: 'timeZone' },
              { id: 6, label: t('address'), key: 'address' },
            ];

            return (
              <>
                <div className="location-actions-wrapper">
                  <MyButton
                    onClickFunc={() => setEditStationOpen(true)}
                    label={t('editStation')}
                    buttonType="outline"
                    svgIcon={<EditIcon />}
                    disableClass="add-device-btn-custom"
                    disableButton={
                      !permissionCheck('partnerChargingStationUpdate') &&
                      !permissionCheck('cpoChargingStationUpdate')
                    }
                  />
                </div>
                <div className="device-general-details-container">
                  <div className="general-details-info-column">
                    {stationInfoDetails.map((generalDetail) => {
                      let displayValue: React.ReactNode = '-';
                      let stringTitle = '';

                      if (generalDetail.key === 'name') {
                        displayValue = stationDetails.name || '-';
                        stringTitle = stationDetails.name || '-';
                      } else if (generalDetail.key === 'isAvailable') {
                        displayValue = stationDetails.isAvailable
                          ? t('available')
                          : t('unavailable');
                        stringTitle = String(displayValue);
                      } else if (generalDetail.key === 'mapLocation') {
                        const locationValue =
                          stationDetails.latitude || stationDetails.longitude
                            ? `${stationDetails.latitude || '-'}, ${stationDetails.longitude || '-'}`
                            : '-';
                        displayValue = locationValue;
                        stringTitle = locationValue;
                      } else if (generalDetail.key === 'emergencyContact') {
                        const contactValue =
                          (stationDetails as any).countryCode ||
                          (stationDetails as any).emergencyContact
                            ? `+${(stationDetails as any).countryCode || ''} ${(stationDetails as any).emergencyContact || ''}`.trim()
                            : '-';
                        displayValue = contactValue;
                        stringTitle = contactValue;
                      } else if (generalDetail.key === 'timeZone') {
                        const tzValue =
                          (stationDetails as any).time_zone ||
                          (stationDetails as any).timeZone ||
                          '-';
                        displayValue = tzValue;
                        stringTitle = tzValue;
                      } else if (generalDetail.key === 'address') {
                        displayValue = (
                          <>
                            <span
                              role="presentation"
                              onClick={() => setLocationPopup('open')}
                              className="location-trigger">
                              <LocationSelector />
                            </span>
                            <span className="address-text">{stationDetails.address || '-'}</span>
                          </>
                        );
                        stringTitle = stationDetails.address || '-';
                      }

                      return (
                        <div className="each-general-detail" key={generalDetail.key}>
                          <div className="general-detail-content">
                            <h1 title={stringTitle}>{displayValue}</h1>
                            <h6>{generalDetail.label}</h6>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="general-details-operating-column">
                    <div className="each-general-detail operating-hours-detail">
                      <div className="general-detail-content">
                        <div className="operating-hours-header">
                          <div className="operating-hours-icon-badge">
                            <ClockIcon className="clock-icon" />
                          </div>
                          <h4 className="operating-hours-title">{t('operatingHours')}</h4>
                        </div>
                        {stationDetails.operatingHours ? (
                          <div className="operating-hours-card">
                            {[
                              { key: 'monday', label: t('monday') },
                              { key: 'tuesday', label: t('tuesday') },
                              { key: 'wednesday', label: t('wednesday') },
                              { key: 'thursday', label: t('thursday') },
                              { key: 'friday', label: t('friday') },
                              { key: 'saturday', label: t('saturday') },
                              { key: 'sunday', label: t('sunday') },
                            ].map((day) => {
                              const dayData = (stationDetails.operatingHours as any)?.[day.key];
                              const isOpen = dayData?.isOpen || dayData?.status === 'open';
                              const formattedHours = getFormattedHours(
                                dayData,
                                t('closed'),
                                t('open24Hours'),
                              );

                              return (
                                <div key={day.key} className="day-schedule-row">
                                  <span className="day-name">{day.label}</span>
                                  <span className={`day-time ${isOpen ? 'open' : 'closed'}`}>
                                    {formattedHours}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <h1 className="no-operating-hours">N/A</h1>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          }

          if (openTab === 1) {
            const directionsList = (stationDetails as any)?.navigationDirections || [];

            return (
              <div className="navigation-directions-tab">
                <div className="directions-header">
                  <div className="header-title-container">
                    <LocationSelector className="directions-icon" />
                    <span className="directions-title">{t('directions')}</span>
                    <span className="directions-count">{directionsList.length}</span>
                  </div>
                  {!isEditingDirections && (
                    <MyButton
                      onClickFunc={handleStartEditDirections}
                      label={t('edit')}
                      buttonType="outline"
                      svgIcon={<EditIcon />}
                      disableClass="add-device-btn-custom"
                      disableButton={
                        !permissionCheck('partnerChargingStationUpdate') &&
                        !permissionCheck('cpoChargingStationUpdate')
                      }
                    />
                  )}
                  {isEditingDirections && directionsListState.length < 4 && (
                    <MyButton
                      onClickFunc={handleAddNavigationDirection}
                      label={`+ ${t('add')}`}
                      buttonType="primary"
                    />
                  )}
                </div>

                {isEditingDirections ? (
                  <div className="navigation-directions-section">
                    <div className="navigation-directions-list">
                      {directionsListState.map((item: any, index: number) => {
                        const availableOptions = getAvailableLanguageOptions(index);
                        return (
                          <div key={index} className="navigation-direction-row">
                            <div className="field-select">
                              <SelectField
                                inputLabel={t('language')}
                                name={`language-${index}`}
                                placeholder={t('selectLanguage')}
                                selectedValue={item.language}
                                onChangeValue={(_, value) => {
                                  const updated = [...directionsListState];
                                  updated[index].language = value as string;
                                  updated[index].direction = '';
                                  setDirectionsListState(updated);
                                }}
                                options={availableOptions}
                                triggerOptions={getLanguageOptions()}
                              />
                            </div>
                            <div className="field-input">
                              <CustomInput
                                inputLabel={t('directions')}
                                type="text"
                                placeholder={
                                  item.language
                                    ? `${t('enterDirectionsIn')} ${item.language}`
                                    : t('selectLanguageFirst')
                                }
                                name={`directions-${index}`}
                                value={item.direction}
                                onChangeValue={(_, value) =>
                                  handleDirectionsChange(index, value as string)
                                }
                                disabled={!item.language}
                                hint={false}
                                error={!!item.language && !item.direction.trim()}
                                errorMessage={t('Directionisrequired')}
                              />
                            </div>
                            {directionsListState.length > 1 && (
                              <button
                                type="button"
                                className="remove-direction-btn"
                                onClick={() => {
                                  setDirectionsListState(
                                    directionsListState.filter(
                                      (_: any, index_: number) => index_ !== index,
                                    ),
                                  );
                                }}>
                                {t('delete')}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div className="directions-edit-actions">
                      <MyButton
                        onClickFunc={() => setIsEditingDirections(false)}
                        label={t('cancel')}
                        buttonType="outline"
                      />
                      <MyButton
                        onClickFunc={handleSaveDirections}
                        label={t('save')}
                        buttonType="primary"
                        loading={isSavingDirections}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="directions-list">
                    {directionsList.length === 0 ? (
                      <div className="no-directions">
                        <p>{t('noDirectionsFound')}</p>
                      </div>
                    ) : (
                      directionsList.map((item: any, index: number) => (
                        <div key={index} className="direction-card">
                          <div className="direction-top-bar">
                            {item.language && (
                              <span className="direction-lang-badge">
                                {item.language === 'Arabic'
                                  ? 'AR'
                                  : item.language.substring(0, 2).toUpperCase()}
                              </span>
                            )}
                            {item.country && (
                              <span className="direction-country-label">{item.country}</span>
                            )}
                          </div>
                          <div className="direction-content">{item.direction}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          }

          return null;
        })()}
      </div>
      {/* Edit Station Panel */}
      <SidePaneModel
        open={editStationOpen}
        closePanel={() => setEditStationOpen(false)}
        content={
          <LocationSidePanel
            fetchIdFrom="props"
            id={selectedChargingPoint?.id as string}
            initialIsEditMode
            onEditModeChange={(isEditMode: any) => {
              if (!isEditMode) setEditStationOpen(false);
            }}
            onClose={() => setEditStationOpen(false)}
            onSuccess={fetchData}
          />
        }
        position="right"
        hideHeader
      />

      <ModalBox
        open={locationPopup === 'open'}
        title={t('selectLocationTitle')}
        content={
          <SelectLatLong
            latitude={stationDetails?.latitude || null}
            longitude={stationDetails?.longitude || null}
            handleLatLongChange={() => setLocationPopup('close')}
          />
        }
        closeModal={() => setLocationPopup('close')}
        header
        modalBoxAnimation="modalAnimateToLeft"
      />
    </div>
  );
}

interface LocationDetailsRightProperties {
  showTabs?: boolean;
  defaultTab?: 'media' | 'amenities';
}

/**
 * Formats a size in bytes to a human-readable string representation
 * @param {any} bytes - The number of bytes to format
 * @param {any} decimalPlaces - The number of decimal places to keep
 * @returns {any} A formatted string with the appropriate size unit (e.g. KB, MB)
 */
const formatBytes = (bytes: number, decimalPlaces = 2) => {
  if (!bytes) return '0 Bytes';
  const kilo = 1024;
  const dmValue = decimalPlaces < 0 ? 0 : decimalPlaces;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const sizeIndex = Math.floor(Math.log(bytes) / Math.log(kilo));
  return `${parseFloat((bytes / kilo ** sizeIndex).toFixed(dmValue))} ${sizes[sizeIndex]}`;
};

/**
 * Station details right panel
 * @param {LocationDetailsRightProperties} properties - Component props
 * @returns {any} The rendered component
 */
export function LocationDetailsRight(properties: LocationDetailsRightProperties) {
  const { showTabs = true, defaultTab = 'media' } = properties;
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<'media' | 'amenities'>(defaultTab);
  const { stationDetails, isLoading } = useAppSelector((state) => state.locationDetails);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  // Image upload modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const previewImages = React.useMemo(() => {
    if (stationDetails?.medias && stationDetails.medias.length > 0) {
      return stationDetails.medias.map((media) => media.signedUrl || media.url);
    }
    return [DefaultStationImage];
  }, [stationDetails?.medias]);

  /**
   * Navigate to previous image in preview carousel
   */
  const handlePreviousImage = () => {
    setSelectedImageIndex((previousIndex) =>
      previousIndex !== null
        ? (previousIndex - 1 + previewImages.length) % previewImages.length
        : 0,
    );
  };

  /**
   * Navigate to next image in preview carousel
   */
  const handleNextImage = () => {
    setSelectedImageIndex((previousIndex) =>
      previousIndex !== null ? (previousIndex + 1) % previewImages.length : 0,
    );
  };

  useEffect(() => {
    if (selectedImageIndex === null) return undefined;
    /**
     *handlekeydown function
     */
    const handleKeyDown = (event_: KeyboardEvent) => {
      if (event_.key === 'ArrowLeft') {
        handlePreviousImage();
      } else if (event_.key === 'ArrowRight') {
        handleNextImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImageIndex, previewImages.length]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const currentTab = showTabs ? activeTab : defaultTab;

  /**
   * Close the edit modal and discard unsaved selections
   */
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  /**
   * Delete a station media image by URL
   * @param {string} mediaUrl - The URL of the media image to delete
   * @param {React.MouseEvent} event_ - The mouse click event
   */
  const handleDeleteMedia = async (mediaUrl: string, event_: React.MouseEvent) => {
    event_.stopPropagation();
    if (!stationDetails?._id || isSaving) return;

    setIsSaving(true);
    const formData = new FormData();
    formData.append('removedUrls[0]', mediaUrl);
    appendStationDataToFormData(formData, stationDetails);

    try {
      await chargingStation.editStation(stationDetails._id, formData, () => {});

      dispatch(
        setNotificationMessage({
          status: true,
          message: t('imageDeletedSuccessfully'),
          type: 'success',
        }),
      );

      dispatch(fetchLocationDetailsData(stationDetails._id));
    } catch (error: any) {
      console.error('Failed to delete station image:', error);
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || error?.message || t('failedToDeleteImage'),
          type: 'error',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="location-details-right-images-amenities" dir={i18n.dir()}>
      {/* Buttons */}
      {showTabs && (
        <div className="location-details-top">
          <MyButton
            label={t('media')}
            buttonType={activeTab === 'media' ? 'submit' : 'primary-outline'}
            onClickFunc={() => setActiveTab('media')}
          />
          <MyButton
            label={t('amenities')}
            buttonType={activeTab === 'amenities' ? 'submit' : 'primary-outline'}
            onClickFunc={() => setActiveTab('amenities')}
          />
        </div>
      )}

      {isLoading ? (
        <div className="skeleton-grid-loader">
          {[...Array(5)].map((index) => (
            <div className="skeleton-card" key={index} />
          ))}
        </div>
      ) : (
        <div
          className={`location-details-bottom ${currentTab === 'amenities' ? 'amenities-tab' : ''}`}>
          {currentTab === 'media' && (
            <div className="location-details-bottom">
              {stationDetails?.medias && stationDetails.medias.length > 0 ? (
                stationDetails.medias.map((media, index) => (
                  <div
                    role="presentation"
                    key={media._id || index}
                    className="image-wrapper"
                    onClick={() => setSelectedImageIndex(index)}>
                    <img
                      src={media.signedUrl || media.url}
                      alt={`${t('station')} ${index}`}
                      onError={(event_) => {
                        const img = event_.currentTarget as HTMLImageElement;
                        img.src = DefaultStationImage;
                      }}
                    />
                    {(permissionCheck('partnerChargingStationDelete') ||
                      permissionCheck('cpoChargingStationDelete')) && (
                      <button
                        type="button"
                        className="delete-media-icon-btn"
                        title={t('delete')}
                        onClick={(event_) => handleDeleteMedia(media.url, event_)}>
                        <DeleteIcon />
                      </button>
                    )}
                    {media.fileSize ? (
                      <div className="image-size-badge">{formatBytes(Number(media.fileSize))}</div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div
                  role="presentation"
                  className="image-wrapper"
                  onClick={() => setSelectedImageIndex(0)}>
                  <img src={DefaultStationImage} alt={t('station')} />
                </div>
              )}

              {/* Edit/Add images trigger card */}
              {(permissionCheck('partnerChargingStationUpdate') ||
                permissionCheck('cpoChargingStationUpdate')) && (
                <div
                  role="presentation"
                  className="image-wrapper add-trigger-card"
                  onClick={() => setIsEditModalOpen(true)}>
                  <div className="add-trigger-content">
                    <span className="plus-icon">+</span>
                    <span className="add-text">{t('addEdit')}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentTab === 'amenities' && (
            <div className="amenity-wrapper">
              <SelectAmenities
                selectedAmenities={stationDetails?.amenities || []}
                handleCancel={() => {}}
                handleStationDetailChange={() => {}}
                hideButtons
                hideRemove
              />
            </div>
          )}
        </div>
      )}

      <ModalBox
        open={selectedImageIndex !== null}
        title={t('imagePreview')}
        closeModal={() => setSelectedImageIndex(null)}
        header
        position="center"
        content={
          <div className="image-preview-modal-content">
            {previewImages.length > 1 && (
              <button
                type="button"
                className="carousel-nav-btn prev-btn"
                aria-label={t('previousImage')}
                onClick={handlePreviousImage}>
                <PreviousArrowIcon />
              </button>
            )}
            <div className="preview-image-container">
              <img
                src={
                  selectedImageIndex !== null && previewImages[selectedImageIndex]
                    ? previewImages[selectedImageIndex]
                    : DefaultStationImage
                }
                alt="Preview"
                onError={(event_) => {
                  const img = event_.currentTarget as HTMLImageElement;
                  img.src = DefaultStationImage;
                }}
              />
              {previewImages.length > 1 && selectedImageIndex !== null && (
                <div className="carousel-counter">
                  {selectedImageIndex + 1} / {previewImages.length}
                </div>
              )}
            </div>
            {previewImages.length > 1 && (
              <button
                type="button"
                className="carousel-nav-btn next-btn"
                aria-label={t('nextImage')}
                onClick={handleNextImage}>
                <NextArrowIcon />
              </button>
            )}
          </div>
        }
      />

      <LocationImagesModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        stationDetails={stationDetails}
        onSuccess={() => {
          if (stationDetails?._id) {
            dispatch(fetchLocationDetailsData(stationDetails._id));
          }
        }}
      />
    </div>
  );
}

LocationDetailsRight.defaultProps = {
  showTabs: true,
  defaultTab: 'media',
};

/**
 * LocationDetails component
 * @returns {any} The rendered component
 */
function LocationDetails() {
  const { i18n } = useTranslation('sidePanel');
  return (
    <div className="location-details-wrap" dir={i18n.dir()}>
      <LocationDetailsLeft />
      <div className="location-details-horizontal-line" />
      <LocationDetailsRight />
    </div>
  );
}

export default LocationDetails;
